<template>
  <div id="signal-checker" class="signal-checker">
    <div v-if="errorURL" class="alerts" style="margin-top: 0; position: relative">
      <div class="alert alert-danger">
        {{ $t("Připojení k aplikaci AlphaGIS trvá příliš dlouho.") }}
        <small>
          {{ $t("Zkontrolujte přístup k") }}
          {{ $t("AlphaGISu") }}
          {{ $t("a obnovte stránku") }}.
        </small>
      </div>
    </div>

    <div class="layout__content__header">
      <h1 class="">
        {{ $t("pokrytiLoRaSignalem") }}
      </h1>
    </div>

    <div class="layout__content__content">
      <div class="mt-4">
        <RadioHelp
          :article-link="'/signal-checker'"
          :show="showHelp"
          @change-radio-btn-visibility="changeRadioBtnVisibility($event)"
          @update-show-radio-help="showRadioHelp($event)"
        />
        <div v-if="radioSlides && radioSlides.length > 0" class="position-relative mt-6">
          <RadioOpenClose
            :custom-class="'d-flex align-items-end radio--default'"
            :radio-slides="radioSlides"
            :show="showHelp"
            @update-show-radio-help="showRadioHelp($event)"
          />
        </div>
      </div>
      <div class="position-relative mb-5 row">
        <AppLoader v-show="viewUpdating || signalRequestInProgress" id="widget-loader" />
        <div
          v-if="view && view.zoom < MAX_ZOOM"
          class="zoom-alert mt-3 p-2 alert-danger align-items-center justify-content-center"
        >
          {{ $t("SIGNAL_AZ_PO_PRIBLIZENI") }}
        </div>
        <div id="map-container" />

        <div id="widget-switch-display-signal" class="esri-widget">
          <div class="dropdown-actions">
            <button class="btn btn-primary" @click="toggleDropdownActions()">
              <div class="d-flex">
                {{ $t("vykonAntenyZarizeni") }}:
                {{ signalLayerStructure[displaySignalLayer].text }}
                <img alt="" class="ml-2" src="@/assets/icons/arrows/arr-down-white.svg" />
              </div>
            </button>
            <div v-if="isActionsDropdownVisible" class="dropdown-actions__wrapper">
              <div class="dropdown-actions__inner">
                <div class="row">
                  <div class="dropdown-actions__item d-flex align-items-center col-12" @click="signalLayerClicked(0)">
                    {{ signalLayerStructure[0].text }}
                  </div>
                  <div
                    class="dropdown-actions__item d-flex align-items-center col-6"
                    @click="signalLayerClicked('lora_0db')"
                  >
                    {{ signalLayerStructure["lora_0db"].text }}
                  </div>
                  <div
                    class="dropdown-actions__item d-flex align-items-center col-6"
                    @click="signalLayerClicked('lora_3db')"
                  >
                    {{ signalLayerStructure["lora_3db"].text }}
                  </div>
                  <div
                    class="dropdown-actions__item d-flex align-items-center col-6"
                    @click="signalLayerClicked('lora_8db')"
                  >
                    {{ signalLayerStructure["lora_8db"].text }}
                  </div>
                  <div
                    class="dropdown-actions__item d-flex align-items-center col-6"
                    @click="signalLayerClicked('lora_15db')"
                  >
                    {{ signalLayerStructure["lora_15db"].text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="widget-switch-display-gws" class="esri-widget layer-switch-widget">
          <div class="custom-control custom-switch">
            <input
              id="switch-display-gws"
              v-model="displayGws"
              class="custom-control-input"
              type="checkbox"
              @change="switchDisplaySignalLayer()"
            />
            <label class="custom-control-label" for="switch-display-gws">
              {{ $t("zobrazitStanice") }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorsToTranslate.length > 0">
      <ErrorModal :centered="true" :errors="errorsToTranslate" :opened="openedError" @close="openedError = false" />
    </div>
  </div>
</template>

<script>
import axios from "axios"
import RadioHelp from "@/components/radio/radio-help.vue"
import RadioOpenClose from "@/components/radio/radio-open-close.vue"
import ErrorModal from "@/components/confirmations/translatedErrors.vue"
import { loadModules } from "esri-loader"
import Loader from "@/components/loader.vue"
import { toRaw } from "vue"
import { useAuthStore } from "@/store/modules/auth"
import { useConfigStore } from "@/store/modules/config"

export default {
  components: {
    RadioHelp,
    RadioOpenClose,
    ErrorModal,
    AppLoader: Loader
  },
  setup() {
    const authStore = useAuthStore()
    const configStore = useConfigStore()

    return { authStore, configStore }
  },
  data() {
    return {
      promises: [],
      errorsToTranslate: [],
      openedError: false,
      radioSlides: [],
      showHelp: true,
      GWS_LAYER_ID: 0,
      mapToken: "",
      INITIAL_EXTENT: {
        xmin: 1342131,
        //xmin: 765131,
        ymin: 6198277,
        //ymin: 6338277,
        xmax: 2102934,
        ymax: 6633612,
        spatialReference: {
          wkid: 102100
        }
      },
      layerOpacity: 0.5,
      MIN_ZOOM: 8,
      MAX_ZOOM: 15,
      SIGNAL_EXTENT_MAX_DIFF: 7250,
      displayGws: true,
      isActionsDropdownVisible: false,
      displaySignalLayer: "lora_0db", // 0 is for no signal-layer, GWs layer is separate
      point: null,
      resultGraphics: null,
      signalProbability: undefined,
      signalLayer: null,
      viewUpdating: false,
      signalRequestInProgress: false,
      errorURL: false
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale === "cz" ? "cs" : this.$i18n.locale
    },
    resultSymbolUrl() {
      return require("@/assets/icons/ico-ico-contact-pin2.svg")
    },
    checkOk() {
      return require("@/assets/icons/actions/ico-ico-check-green.svg")
    },
    checkKo() {
      return require("@/assets/icons/actions/ico-delete-2.svg")
    },
    devicePlacementOptionsTexts() {
      return [
        this.$t("hlubokyInterier"),
        this.$t("interier"),
        this.$t("exterierNeprimaViditelnost"),
        this.$t("exterierPrimaViditelnost")
      ]
    },
    radioDisabled() {
      return this.configStore.isRadioHelpDisabled
    },
    signalLayerStructure() {
      const structure = {}

      structure[0] = {
        text: this.$t("bezVizualizace")
      }
      structure["lora_0db"] = {
        text: this.$t("velmiSilny"),
        devicePlacementOptionsRanges: [
          {
            from: 86,
            to: 96
          },
          {
            from: 97,
            to: 106
          },
          {
            from: 107,
            to: 116
          },
          {
            from: 117,
            to: 124
          }
        ]
      }
      structure["lora_3db"] = {
        text: this.$t("silny"),
        devicePlacementOptionsRanges: [
          {
            from: 86,
            to: 94
          },
          {
            from: 95,
            to: 104
          },
          {
            from: 105,
            to: 114
          },
          {
            from: 115,
            to: 122
          }
        ]
      }
      structure["lora_8db"] = {
        text: this.$t("slaby"),
        devicePlacementOptionsRanges: [
          {
            from: 86,
            to: 90
          },
          {
            from: 91,
            to: 100
          },
          {
            from: 101,
            to: 110
          },
          {
            from: 111,
            to: 118
          }
        ]
      }
      structure["lora_15db"] = {
        text: this.$t("velmiSlaby"),
        devicePlacementOptionsRanges: [
          {
            from: 84,
            to: 85
          },
          {
            from: 86,
            to: 92
          },
          {
            from: 93,
            to: 102
          },
          {
            from: 103,
            to: 112
          }
        ]
      }

      return structure
    }
  },
  watch: {
    radioDisabled() {
      this.showHelp = !this.radioDisabled
    },
    locale() {
      if (this.intlModule) {
        this.intlModule.setLocale(this.locale)

        if (this.search) {
          this.search.destroy()
        }

        this.initSearch()
      }
    }
  },
  mounted() {
    this.showHelp = !this.radioDisabled

    this.errorsToTranslate = []
    loadModules(
      [
        "esri/config",
        "esri/identity/IdentityManager",
        "esri/intl",
        //"esri/Map",
        "esri/WebMap",
        "esri/views/MapView",
        "esri/widgets/Search",
        "esri/core/watchUtils",
        "esri/geometry/Extent",
        "esri/layers/MapImageLayer",
        "esri/core/urlUtils"
      ],
      { css: true }
    ).then(
      async ([
        Config,
        IdentityManager,
        Intl,
        //ArcGISMap,
        WebMap,
        MapView,
        Search,
        WatchUtils,
        Extent,
        MapImageLayer,
        UrlUtils
      ]) => {
        this.intlModule = Intl
        this.searchModule = Search
        this.extentModule = Extent
        this.signalLayers = {
          gw: {
            id: "",
            content: {}
          },
          lora_0db: {
            id: "",
            content: {}
          },
          lora_3db: {
            id: "",
            content: {}
          },
          lora_8db: {
            id: "",
            content: {}
          },
          lora_15db: {
            id: "",
            content: {}
          }
        }
        this.$store.commit("modals/setWaitingLoader", this.$t("Načítám mapová data, čekejte prosím..."))
        /**
         * wait for token request
         */
        let result = await axios.post(process.env.VUE_APP_APICMS + "/getMapToken/", { token: this.authStore.user.access_token })
        if (result.data.error) {
          this.errorsToTranslate.push({
            errors: [result.data.error]
          })
        } else this.mapToken = result.data.token

        /**
         * find and set up map layer ids
         */
        result = await axios.get(
          process.env.VUE_APP_MAP_PROXY +
            "?https://www.arcgis.com/sharing/rest/content/items/" +
            process.env.VUE_APP_ARCGISMAP_MAP_ID +
            "/data?f=json"
        )
        if (result.data.operationalLayers) {
          for (const layer in this.signalLayers) {
            result.data.operationalLayers.forEach((l) => {
              if (layer === l.title) {
                this.signalLayers[layer].id = l.itemId
              }
            })
          }
        }

        /**
         * we have token
         */
        let proxyUrl = process.env.VUE_APP_MAP_PROXY
        Config.request.proxyUrl = proxyUrl
        Config.request.alwaysUseProxy = true
        UrlUtils.addProxyRule({
          urlPrefix: "services3.arcgis.com",
          proxyUrl: proxyUrl
        })
        UrlUtils.addProxyRule({
          urlPrefix: "www.arcgis.com",
          proxyUrl: proxyUrl
        })
        UrlUtils.addProxyRule({
          urlPrefix: "tiles.arcgis.com",
          proxyUrl: proxyUrl
        })

        /**
         * we must redister token received from cms api
         */
        IdentityManager.registerToken({
          server: "https://www.arcgis.com/sharing/rest",
          token: this.mapToken
        })

        /**
         * wait for layers load
         */
        for (const layer in this.signalLayers) {
          this.signalLayers[layer].content = await MapImageLayer.fromPortalItem({
            portalItem: {
              id: this.signalLayers[layer].id
            }
          })
          this.signalLayers[layer].content.visible = true
          this.signalLayers[layer].content.opacity = layer === "gw" ? 1 : this.layerOpacity
        }

        this.intlModule.setLocale(this.locale)

        Config.apiKey = process.env.VUE_APP_ARCGISMAP_APIKEY

        this.map = new WebMap({
          portalItem: {
            id: process.env.VUE_APP_ARCGISMAP_MAP_ID
          }
        })

        this.view = new MapView({
          container: "map-container",
          map: toRaw(this.map),
          extent: this.INITIAL_EXTENT,
          constraints: {
            rotationEnabled: false,
            minZoom: this.MIN_ZOOM,
            maxZoom: this.MAX_ZOOM
          },
          popup: {
            actions: null,
            dockOptions: {
              buttonEnabled: false
            }
          },
          navigation: {
            mouseWheelZoomEnabled: false
          }
        })

        this.view.on("click", (ev) => {
          this.point = ev.mapPoint

          this.search.clear()

          this.queryValue()
        })

        this.view.on("focus", (ev) => {
          ev.native.srcElement.blur()
        })

        WatchUtils.whenTrue(this.view, "stationary", () => {
          this.switchDisplaySignalLayer()
        })

        WatchUtils.whenTrue(this.view, "updating", () => {
          this.viewUpdating = true
        })

        WatchUtils.whenFalse(this.view, "updating", () => {
          this.viewUpdating = false
          this.$store.commit("modals/setWaitingLoader", "")
        })

        this.initSearch()

        this.view.ui.move("zoom", "top-left")

        this.view.ui.add(document.getElementById("widget-switch-display-signal"), "top-right")
        this.view.ui.add(document.getElementById("widget-switch-display-gws"), "bottom-right")
        this.view.ui.add(document.getElementById("widget-loader"), "manual")

        // TODO nacist vrstvu ulozenou v local storage
        this.switchDisplaySignalLayer()
      }
    )
  },
  beforeUnmount() {
    if (this.view) {
      this.view.container = null
    }
  },
  methods: {
    changeRadioBtnVisibility(radioSlides) {
      this.radioSlides = radioSlides
    },
    showRadioHelp(status) {
      this.showHelp = status
    },
    initSearch() {
      const tmpSearch = new this.searchModule({
        view: toRaw(this.view),
        popupEnabled: false,
        resultGraphicEnabled: false
      })

      tmpSearch.goToOverride = function () {
        return false
      }

      tmpSearch.on("search-complete", (ev) => {
        this.point = ev.results[0].results[0].target.geometry.centroid

        this.moveAndZoomExtent()

        this.queryValue()
      })

      this.search = tmpSearch

      const tmpView = toRaw(this.view)
      tmpView.ui.add(toRaw(this.search), "top-left")
      this.view = tmpView
    },
    moveAndZoomExtent() {
      let paddingFactor = 0.8

      let widthToHeight = toRaw(this.view).extent.width / toRaw(this.view).extent.height
      let width = this.SIGNAL_EXTENT_MAX_DIFF * paddingFactor
      let height = this.SIGNAL_EXTENT_MAX_DIFF * paddingFactor

      if (widthToHeight > 1) {
        height /= widthToHeight
      } else {
        width *= widthToHeight
      }

      const tmpView = toRaw(this.view)
      tmpView.extent = new this.extentModule({
        xmin: toRaw(this.point).x - width / 2,
        xmax: toRaw(this.point).x + width / 2,
        ymin: toRaw(this.point).y - height / 2,
        ymax: toRaw(this.point).y + height / 2,
        spatialReference: toRaw(tmpView.extent.spatialReference)
      })

      this.view = tmpView
    },
    setMarker() {
      const tmpView = toRaw(this.view)
      tmpView.graphics = toRaw(tmpView.graphics)
      tmpView.graphics.removeAll()
      tmpView.graphics.add({
        geometry: {
          type: "point",
          latitude: toRaw(this.point).latitude,
          longitude: toRaw(this.point).longitude
        },
        symbol: {
          type: "picture-marker",
          url: toRaw(this.resultSymbolUrl),
          width: "23px",
          height: "30px",
          yoffset: "15px"
        }
      })
      this.view = tmpView
    },
    showPopup() {
      let devicePlacementResult = ""

      if (this.displaySignalLayer) {
        const devicePlacementOptionsRanges = toRaw(
          this.signalLayerStructure[this.displaySignalLayer].devicePlacementOptionsRanges
        )

        for (let i in devicePlacementOptionsRanges) {
          let coverageIconUrl = toRaw(this.checkKo)

          if (-this.signalProbability <= devicePlacementOptionsRanges[i].to) {
            coverageIconUrl = toRaw(this.checkOk)
          }

          devicePlacementResult += `<div class="device-placement-option" style="background-image: url('${coverageIconUrl}')">
                                        ${toRaw(this.devicePlacementOptionsTexts)[i]}
                                    </div>`
        }
      }

      const tmpView = toRaw(this.view)
      tmpView.popup.open({
        title: this.$t("vysledek"),
        content:
          `<div>
                      ${this.$t("signalVMiste")}
                      ${toRaw(this.point).latitude.toFixed(5)} °, ${toRaw(this.point).longitude.toFixed(5)} °
                      : <b>${
                        isNaN(this.signalProbability) ? this.$t("zadnySignal") : toRaw(this.signalProbability) + " dB"
                      }</b>
                  </div>` + toRaw(devicePlacementResult),
        location: toRaw(this.point)
      })
      this.view = tmpView
    },
    queryValue() {
      if (this.point) {
        this.setMarker()

        const signalRequestUrl =
          process.env.VUE_APP_MAP_PROXY +
          "?" +
          process.env.VUE_APP_ARCGISMAP_POINT_SIGNAL +
          "&geometry=" +
          toRaw(this.point).longitude +
          "%2C" +
          toRaw(this.point).latitude +
          "&token=" +
          this.mapToken

        this.signalRequestInProgress = true

        axios
          .get(signalRequestUrl)
          .then((response) => {
            /*
            if (response.data.code) {
              throw response.data;
            }
            */
            if (response.data.features[0]) {
              this.signalProbability = -parseFloat(response.data.features[0].attributes.gridcode)
            } else this.signalProbability = NaN

            this.signalRequestInProgress = false

            this.showPopup()
          })
          .catch((er) => {
            this.signalProbability = undefined

            this.signalRequestInProgress = false

            if (er.code === 303) {
              this.showPopup()
            } else {
              //this.errorURL = true;
            }
          })
      }
    },
    toggleDropdownActions() {
      this.isActionsDropdownVisible = !this.isActionsDropdownVisible
    },
    switchDisplaySignalLayer() {
      this.map.removeAll()
      this.map.add(this.signalLayers[this.displaySignalLayer].content)

      if (this.displayGws) {
        this.map.add(this.signalLayers["gw"].content)
      }

      this.queryValue()
    },
    signalLayerClicked(displaySignalLayer) {
      this.displaySignalLayer = displaySignalLayer
      this.toggleDropdownActions()
      this.switchDisplaySignalLayer()
    }
  }
}
</script>

<style lang="scss">
.esri-popup__content {
  .device-placement-option {
    padding: 10px 0 0 30px;
    background-repeat: no-repeat;
    background-position-y: 12px;
  }
}

.esri-input.esri-search__input[type="text"],
.esri-widget--button.esri-search__submit-button {
  height: 47px;
}

.esri-widget,
.esri-search__input {
  border-radius: 0.25rem;

  .esri-search__submit-button {
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  &.esri-zoom .esri-widget--button:not(:first-child) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}
</style>

<style lang="scss" scoped>
.zoom-alert {
  position: absolute;
  top: 0;
  left: 50%;
  margin-left: -250px;
  z-index: 1;

  @include media-breakpoint-down(md) {
    position: relative;
    left: 0;
    margin: 10px auto;
  }
}

#map-container {
  padding: 0;
  margin: 0;
  width: 100%;
  height: calc(100vh - 180px);

  @include media-breakpoint-down(sm) {
    height: calc(100vh - 150px);
  }

  min-height: 400px;
}

.dropdown-actions__item {
  justify-content: center;
}

.layer-switch-widget {
  z-index: 99;
  background-color: $white;
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
  line-height: 1.35rem;

  .custom-switch {
    padding-left: 3.6rem;

    .custom-control-label::before {
      left: -3.1rem;
    }

    .custom-control-label::after {
      left: -58px;
    }

    .custom-control-input:checked ~ .custom-control-label::after {
      left: -48px;
    }
  }
}

#widget-loader {
  position: relative;
  top: calc(50% - 10px);
  left: calc(50% - 30px);
  z-index: 1000;
}
</style>
